import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';

import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import { CollectionActivityInitialState } from '../globalSlices/collectionActivitySlice';
import {
  getBuyerCollectionActivities,
  getSupplierCollectionActivities,
  getSupplierCollectionTasks,
  createCollectionActivity,
  updateCollectionActivity,
  deleteCollectionActivity,
} from '../services/collectionActivityService';
import { CollectionActivityWithUser } from '@dill/dill-shared';
import { decideTaxExemptReview } from '../services/creditApplicationsService';

export const getBuyerCollectionActivitiesExtraReducer = (
  builder: ActionReducerMapBuilder<CollectionActivityInitialState>
) => {
  const reducerId = 'getBuyerCollectionActivities';
  builder.addCase(getBuyerCollectionActivities.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    getBuyerCollectionActivities.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        state.buyerActivities = action.payload.data as CollectionActivityWithUser[];
      }
    }
  );
  builder.addCase(getBuyerCollectionActivities.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const getSupplierCollectionActivitiesExtraReducer = (
  builder: ActionReducerMapBuilder<CollectionActivityInitialState>
) => {
  const reducerId = 'getSupplierCollectionActivities';
  builder.addCase(getSupplierCollectionActivities.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    getSupplierCollectionActivities.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        state.supplierActivities = action.payload.data as CollectionActivityWithUser[];
      }
    }
  );
  builder.addCase(getSupplierCollectionActivities.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const createCollectionActivityExtraReducer = (
  builder: ActionReducerMapBuilder<CollectionActivityInitialState>
) => {
  const reducerId = 'createCollectionActivity';
  builder.addCase(createCollectionActivity.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    createCollectionActivity.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        const newActivity = action.payload.data as CollectionActivityWithUser;
        state.buyerActivities = [newActivity, ...state.buyerActivities];
        state.supplierActivities = [newActivity, ...state.supplierActivities];
      }
    }
  );
  builder.addCase(createCollectionActivity.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const updateCollectionActivityExtraReducer = (
  builder: ActionReducerMapBuilder<CollectionActivityInitialState>
) => {
  const reducerId = 'updateCollectionActivity';
  builder.addCase(updateCollectionActivity.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(updateCollectionActivity.fulfilled, (state, action) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    // Applied locally from the request the caller already made, rather than waiting on a
    // full refetch - the update endpoint only returns { success }, not the updated activity,
    // and a refetch would flip `loadingList`/DillTable's `loading` prop, blanking the whole
    // table for a moment on every accept/decline/start/complete click.
    if ((action.payload as AppServerResponse | null)?.success) {
      const { activityId, updateData } = action.meta.arg;
      const applyUpdate = (activity: CollectionActivityWithUser) =>
        activity.id === activityId ? { ...activity, ...updateData } : activity;
      state.buyerActivities = state.buyerActivities.map(applyUpdate);
      state.supplierActivities = state.supplierActivities.map(applyUpdate);
      state.supplierTasks = state.supplierTasks.map(applyUpdate);
    }
  });
  builder.addCase(updateCollectionActivity.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const deleteCollectionActivityExtraReducer = (
  builder: ActionReducerMapBuilder<CollectionActivityInitialState>
) => {
  const reducerId = 'deleteCollectionActivity';
  builder.addCase(deleteCollectionActivity.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(deleteCollectionActivity.fulfilled, (state, action) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    if ((action.payload as AppServerResponse | null)?.success) {
      const { activityId } = action.meta.arg;
      state.buyerActivities = state.buyerActivities.filter((a) => a.id !== activityId);
      state.supplierActivities = state.supplierActivities.filter((a) => a.id !== activityId);
      state.supplierTasks = state.supplierTasks.filter((a) => a.id !== activityId);
    }
  });
  builder.addCase(deleteCollectionActivity.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const getSupplierCollectionTasksExtraReducer = (
  builder: ActionReducerMapBuilder<CollectionActivityInitialState>
) => {
  const reducerId = 'getSupplierCollectionTasks';
  builder.addCase(getSupplierCollectionTasks.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    getSupplierCollectionTasks.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        state.supplierTasks = action.payload.data as CollectionActivityWithUser[];
      }
    }
  );
  builder.addCase(getSupplierCollectionTasks.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

// A tax exemption decision completes every review task for the application (one per team
// member); the endpoint returns them so the tasks list updates without a refetch.
export const decideTaxExemptReviewTasksExtraReducer = (
  builder: ActionReducerMapBuilder<CollectionActivityInitialState>
) => {
  builder.addCase(decideTaxExemptReview.fulfilled, (state, action) => {
    const tasks = ((action.payload as AppServerResponse | null)?.data?.tasks ??
      []) as CollectionActivityWithUser[];
    if (!tasks.length) return;
    const applyUpdate = (activity: CollectionActivityWithUser) => {
      const updated = tasks.find((task) => task.id === activity.id);
      return updated ? { ...activity, taskDetails: updated.taskDetails } : activity;
    };
    state.supplierTasks = state.supplierTasks.map(applyUpdate);
    state.supplierActivities = state.supplierActivities.map(applyUpdate);
  });
};
